/**
 * Vistas previas de los libros fiscales (pedido del usuario del 2026-10-30): lo que una pantalla muestra ANTES de
 * guardar, sacado de los datos que el servidor ya devolvió. No calcula impuestos ni montos nuevos: reparte y suma lo que
 * la compra o el documento ya trae, con las mismas reglas que aplica el libro. Lo usan la pantalla (para mostrar) y las
 * pruebas de la API (que comprueban que, después de guardar, el libro dice lo mismo).
 */
import { fechaCaracas, fechaRetencion, quincenaDe } from './fechas.ts';
import { Money } from './money/index.ts';

/** Lo que la vista de numeración necesita de cada comprobante de la compra. */
export interface ComprobanteVistaLibros {
  tipo: 'iva' | 'islr';
  estado: string;
  numero: string | null;
  periodo: string;
  monto_bs: string;
}

export type VistaNumerarComprobantes =
  | { ok: false; motivo: 'periodo_invalido' | 'periodo_futuro' | 'periodo_anterior' | 'sin_provisionales' }
  | {
      ok: true;
      periodo: string;
      /** El período es el de la compra: lo retenido llena la fila de la factura. Si no, sale una fila «Retención». */
      misma_fila: boolean;
      /** Fecha de la retención: la de la fila «Retención», la que decide la quincena del TXT y la del XML del ISLR. */
      fecha_retencion: string;
      quincena: '1' | '2';
      /** Lo que el libro de ese período lleva de esta compra (lo que se numera ahora más lo ya numerado en ese período). */
      iva_bs: string;
      islr_bs: string;
      /** Cuántos comprobantes se numeran ahora, por tipo. */
      provisionales: { iva: number; islr: number };
    };

/**
 * Numerar los comprobantes provisionales de una compra en el período `periodo` (AAAAMM), en el instante `ahora`. Las
 * reglas son las de `numerarComprobantes` (no futuro, no anterior al período de la compra, numera todos los
 * provisionales) y las de `libroCompras`, `txtPeriodo` y `xmlIslrPeriodo` (`apps/api/src/modulos/compras/casos.ts` y
 * `reportes/libros.ts`): en el período de la compra va en su fila; en otro, fila «Retención» con la fecha de la retención.
 */
export function vistaNumerarComprobantes(
  compra: { periodo: string; fecha_factura: string; fecha_fiscal: string | null; comprobantes: ComprobanteVistaLibros[] },
  periodo: string,
  ahora: Date,
): VistaNumerarComprobantes {
  const mes = Number(periodo.slice(4, 6));
  if (!/^\d{6}$/.test(periodo) || mes < 1 || mes > 12) return { ok: false, motivo: 'periodo_invalido' };
  if (periodo > fechaCaracas(ahora).slice(0, 7).replace('-', '')) return { ok: false, motivo: 'periodo_futuro' };
  if (periodo < compra.periodo) return { ok: false, motivo: 'periodo_anterior' };
  const vigentes = compra.comprobantes.filter((c) => c.estado === 'vigente');
  const provisionales = vigentes.filter((c) => !c.numero);
  if (provisionales.length === 0) return { ok: false, motivo: 'sin_provisionales' };
  const enPeriodo = vigentes.filter((c) => !c.numero || c.periodo === periodo);
  const suma = (tipo: 'iva' | 'islr') => Money.sumar(enPeriodo.filter((c) => c.tipo === tipo).map((c) => Money.parse(c.monto_bs))).redondear2().toDocumento();
  const fecha = fechaRetencion(compra.fecha_fiscal ?? compra.fecha_factura, ahora, periodo);
  return {
    ok: true,
    periodo,
    misma_fila: periodo === compra.periodo,
    fecha_retencion: fecha,
    quincena: quincenaDe(fecha),
    iva_bs: suma('iva'),
    islr_bs: suma('islr'),
    provisionales: { iva: provisionales.filter((c) => c.tipo === 'iva').length, islr: provisionales.filter((c) => c.tipo === 'islr').length },
  };
}

/** Columna y concepto de un renglón de ajuste (o de conteo) en el libro de inventario. */
export interface ClaseAjusteLibro {
  clase: 'inicial' | 'sobrante' | 'uso_interno' | 'merma' | 'otro_retiro';
  columna: 'entradas' | 'retiros' | 'autoconsumos';
  concepto: string;
  /** Entra también al libro de inventario fiscal: solo el inventario inicial. */
  fiscal: boolean;
}

/**
 * La misma clasificación que hace el libro de inventario con el kardex de un ajuste (`reportes/inventario-libro.ts`, el
 * CASE de `leer`, y los conceptos de `CLASES`): lo que entra con «Inventario inicial» es inventario inicial (el único que
 * va también al fiscal); lo demás que entra, sobrante; lo que sale, uso interno, rotura o merma, o diferencia de conteo
 * y otros retiros. La prueba `vista-libros-ajuste.spec.ts` comprueba motivo por motivo que el libro dice lo mismo.
 */
export function claseAjusteLibro(motivo: string, cantidad: string): ClaseAjusteLibro {
  const entra = Money.parse(cantidad).esPositivo();
  if (entra && motivo === 'Inventario inicial') return { clase: 'inicial', columna: 'entradas', concepto: 'Inventario inicial', fiscal: true };
  if (entra) return { clase: 'sobrante', columna: 'entradas', concepto: 'Sobrantes y ajustes', fiscal: false };
  if (motivo === 'Uso interno') return { clase: 'uso_interno', columna: 'autoconsumos', concepto: 'Uso interno', fiscal: false };
  if (['Rotura', 'Merma', 'Daño', 'Pérdida', 'Diferencia de traslado'].includes(motivo)) return { clase: 'merma', columna: 'retiros', concepto: 'Roturas, mermas, daños y pérdidas', fiscal: false };
  return { clase: 'otro_retiro', columna: 'retiros', concepto: 'Diferencias de conteo y otros retiros', fiscal: false };
}

/** Lo que la vista de una devolución necesita del cálculo (`POST /devoluciones/calcular`). */
export interface DevolucionVistaLibros {
  tipo_resultante: 'NC' | 'CAM';
  base_bs: string;
  iva_bs: string;
  total_bs: string;
  lineas: { producto_codigo: string; producto_nombre: string; unidades: string; inventario: 'producto' | 'lote' | 'pieza' | 'servicio'; danada: boolean }[];
}

export interface VistaDevolucionLibros {
  /** La fila de la nota de crédito en el libro de ventas (restando); null para el «Cambio», que no es fiscal. */
  libro_ventas: { total_con_iva: string; exento: string; base: string; iva: string; igtf: string; iva_retenido: string; retencion_municipal: string } | null;
  /** Lo que entra (y, si vino dañado, sale) del libro de inventario real. */
  inventario_real: { codigo: string; nombre: string; columna: 'entradas' | 'retiros'; concepto: string; unidades: string }[];
  /** Lo que entra al libro de inventario fiscal: solo con nota de crédito de una factura. */
  inventario_fiscal: { codigo: string; nombre: string; concepto: string; unidades: string }[];
}

/**
 * Cómo entra una devolución a los libros (reglas de `libroVentas` en `reportes/libros.ts` y de los dos libros de
 * inventario en `reportes/inventario-libro.ts`): la nota de crédito resta en el libro de ventas con sus Bs guardados, sin
 * exento, sin IGTF y sin retención; el «Cambio» no entra. En el inventario real entra todo lo devuelto (producto o lote)
 * y lo dañado sale en el acto como retiro; en el fiscal, solo con nota de crédito, entra lo devuelto completo (también lo
 * dañado). Las piezas de vidrio y los servicios no entran. Sus valores en Bs van al costo promedio del mes.
 */
const negativo = (v: string) => Money.parse(v).neg().toDocumento();

export function vistaDevolucionLibros(calculo: DevolucionVistaLibros): VistaDevolucionLibros {
  const nc = calculo.tipo_resultante === 'NC';
  const conInventario = calculo.lineas.filter((l) => l.inventario === 'producto' || l.inventario === 'lote');
  const real: VistaDevolucionLibros['inventario_real'] = [];
  for (const l of conInventario) {
    real.push({ codigo: l.producto_codigo, nombre: l.producto_nombre, columna: 'entradas', concepto: 'Devoluciones de clientes', unidades: l.unidades });
    if (l.danada) real.push({ codigo: l.producto_codigo, nombre: l.producto_nombre, columna: 'retiros', concepto: l.inventario === 'lote' ? 'Bajas de productos con detalles' : 'Roturas, mermas, daños y pérdidas', unidades: l.unidades });
  }
  return {
    libro_ventas: nc ? { total_con_iva: negativo(calculo.total_bs), exento: '0.00', base: negativo(calculo.base_bs), iva: negativo(calculo.iva_bs), igtf: '0.00', iva_retenido: '0.00', retencion_municipal: '0.00' } : null,
    inventario_real: real,
    inventario_fiscal: nc ? conInventario.map((l) => ({ codigo: l.producto_codigo, nombre: l.producto_nombre, concepto: 'Notas de crédito de facturas', unidades: l.unidades })) : [],
  };
}
